import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import Box from "@mui/material/Box";
import {
  DEFAULT_SUBTITLE_OPTIONS,
  SUBTITLE_PRESET_BOXED,
  SUBTITLE_PRESET_HIGHLIGHT,
  SUBTITLE_PRESET_KARAOKE,
  resolveSubtitlePreset,
} from "../../components/Modal/ModalCreateWithIA/subtitlePresets";
import { useDispatchApp, useSelectorApp } from "../../lib/redux";
import {
  applyVideoEditorEdl,
  commitVideoEditorClipUpdate,
} from "../../actions/videoEditor";
import {
  selectVideoEditorEdl,
  selectVideoEditorPlayhead,
} from "./videoEditorSelectors";
import { VIDEO_EDITOR_TRACK_TYPE } from "./constants";
import { useVideoEditorPlaybackTime } from "./videoEditorPlaybackClock";
import {
  buildSubtitleClipTextUpdates,
  findActiveSubtitleClip,
  areEditorSubtitlesEnabled,
} from "./subtitleEdl";
import {
  applySubtitleAnchorDom,
  clampSubtitleAnchor,
  clearSubtitleAnchorDom,
  clientPointToAnchor,
  getPreviewSubtitleAnchor,
} from "./subtitleOverlayLayout";

const loadedGoogleFonts = new Set();
const DRAG_THRESHOLD_PX = 4;
const LINE_MAX_WIDTH = "78%";

const loadGoogleFont = (fontFamily) => {
  if (!fontFamily || loadedGoogleFonts.has(fontFamily)) {
    return;
  }
  loadedGoogleFonts.add(fontFamily);
  const familyParam = String(fontFamily).replace(/ /g, "+");
  const link = document.createElement("link");
  link.rel = "stylesheet";
  link.href = `https://fonts.googleapis.com/css2?family=${familyParam}:ital,wght@0,400;0,700;1,400;1,700&display=swap`;
  document.head.appendChild(link);
};

const getFontStyleFromOption = (fontStyle) => {
  switch (fontStyle) {
    case "bold":
      return { fontWeight: 700, fontStyle: "normal" };
    case "italic":
      return { fontWeight: 400, fontStyle: "italic" };
    case "bold_italic":
      return { fontWeight: 700, fontStyle: "italic" };
    case "normal":
      return { fontWeight: 400, fontStyle: "normal" };
    default:
      return { fontWeight: 700, fontStyle: "normal" };
  }
};

const TEXT_STROKE =
  "0.04em 0.04em 0 #000, -0.04em -0.04em 0 #000, 0.04em -0.04em 0 #000, -0.04em 0.04em 0 #000, 0.08em 0 0 #000, -0.08em 0 0 #000, 0 0.08em 0 #000, 0 -0.08em 0 #000";

  // eslint-disable-next-line
const VideoEditorSubtitleOverlay = ({
  stageRef,
  isPlaying = false,
  onPauseForEdit = () => {},
}) => {
  const dispatch = useDispatchApp();
  const edl = useSelectorApp(selectVideoEditorEdl);
  const reduxPlayhead = useSelectorApp(selectVideoEditorPlayhead);
  const playhead = useVideoEditorPlaybackTime(reduxPlayhead);
  const inputRef = useRef(null);
  const blockRef = useRef(null);
  const dragRef = useRef(null);
  const rafRef = useRef(null);
  const ignoreBlurRef = useRef(false);
  const edlRef = useRef(edl);
  edlRef.current = edl;
  const [isEditing, setIsEditing] = useState(false);
  const [draftText, setDraftText] = useState("");

  const subtitleClips = useMemo(
    () =>
      Object.values(edl?.clips?.byId || {}).filter(
        (clip) => clip.type === VIDEO_EDITOR_TRACK_TYPE.SUBTITLE
      ),
    [edl?.clips]
  );

  const activeClip = useMemo(
    () => findActiveSubtitleClip({ clips: subtitleClips, playhead }),
    [playhead, subtitleClips]
  );

  const options = edl?.subtitleOptions || DEFAULT_SUBTITLE_OPTIONS;
  const preset = resolveSubtitlePreset(options);
  const isBoxed = preset === SUBTITLE_PRESET_BOXED;
  const fontStyles = getFontStyleFromOption(options.fontStyle);
  const text = activeClip?.text || activeClip?.label || "";
  const words =
    Array.isArray(activeClip?.words) && activeClip.words.length > 0
      ? activeClip.words
      : text
          .split(/\s+/)
          .filter(Boolean)
          .map((word) => ({ word }));
  const anchor = getPreviewSubtitleAnchor(options);

  useEffect(() => {
    loadGoogleFont(options.fontFamily);
  }, [options.fontFamily]);

  useEffect(() => {
    if (dragRef.current) {
      return;
    }
    clearSubtitleAnchorDom(blockRef.current);
  }, [options?.position?.x, options?.position?.y]);

  useEffect(() => {
    setIsEditing(false);
    setDraftText(text);
  }, [activeClip?.id, text]);

  useEffect(() => {
    if (!isEditing) {
      return undefined;
    }
    const timeoutId = window.setTimeout(() => {
      inputRef.current?.focus();
    }, 50);
    return () => window.clearTimeout(timeoutId);
  }, [activeClip?.id, isEditing]);

  const activeWordIndex = useMemo(() => {
    if (!activeClip?.words?.length) {
      return -1;
    }
    const time = Number(playhead) || 0;
    return activeClip.words.findIndex(
      (word) => time >= Number(word.start) && time < Number(word.end)
    );
  }, [activeClip, playhead]);

  const commitPosition = useCallback((nextAnchor) => {
    const currentEdl = edlRef.current;
    if (!currentEdl) {
      return;
    }
    dispatch(
      applyVideoEditorEdl({
        edl: {
          ...currentEdl,
          subtitleOptions: {
            ...(currentEdl.subtitleOptions || DEFAULT_SUBTITLE_OPTIONS),
            position: clampSubtitleAnchor(nextAnchor),
          },
        },
      })
    );
  }, [dispatch]);

  const commitText = useCallback(() => {
    if (ignoreBlurRef.current) {
      ignoreBlurRef.current = false;
      setIsEditing(false);
      setDraftText(text);
      return;
    }
    const nextText = String(draftText || "").trim();
    setIsEditing(false);
    if (!activeClip || !nextText) {
      setDraftText(text);
      return;
    }
    if (nextText === text) {
      return;
    }
    dispatch(
      commitVideoEditorClipUpdate({
        clipId: activeClip.id,
        updates: buildSubtitleClipTextUpdates({
          clip: activeClip,
          text: nextText,
        }),
      })
    );
  }, [activeClip, dispatch, draftText, text]);

  const stopRaf = useCallback(() => {
    if (!rafRef.current) {
      return;
    }
    window.cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
  }, []);

  const applyDragDom = useCallback(() => {
    rafRef.current = null;
    const drag = dragRef.current;
    if (!drag) {
      return;
    }
    applySubtitleAnchorDom(blockRef.current, drag.nextAnchor);
  }, []);

  const onBlockPointerDown = useCallback(
    (event) => {
      if (isPlaying || event.button === 2) {
        return;
      }
      if (event.target?.closest?.("input")) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      onPauseForEdit();

      const stage = stageRef?.current;
      if (!stage) {
        return;
      }

      const startAnchor = getPreviewSubtitleAnchor(
        edlRef.current?.subtitleOptions
      );
      const startPoint = clientPointToAnchor({ event, stage });
      dragRef.current = {
        pointerId: event.pointerId,
        originX: event.clientX,
        originY: event.clientY,
        hasMoved: false,
        grabOffset: {
          x: startPoint.x - startAnchor.x,
          y: startPoint.y - startAnchor.y,
        },
        nextAnchor: startAnchor,
      };
      event.currentTarget.setPointerCapture?.(event.pointerId);
    },
    [isPlaying, onPauseForEdit, stageRef]
  );

  useEffect(() => {
    const onPointerMove = (event) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) {
        return;
      }

      const distance = Math.hypot(
        event.clientX - drag.originX,
        event.clientY - drag.originY
      );
      if (!drag.hasMoved && distance < DRAG_THRESHOLD_PX) {
        return;
      }

      if (!drag.hasMoved) {
        drag.hasMoved = true;
        setIsEditing(false);
      }

      const stage = stageRef?.current;
      if (!stage) {
        return;
      }

      const point = clientPointToAnchor({ event, stage });
      drag.nextAnchor = clampSubtitleAnchor({
        x: point.x - drag.grabOffset.x,
        y: point.y - drag.grabOffset.y,
      });

      if (rafRef.current) {
        return;
      }
      rafRef.current = window.requestAnimationFrame(applyDragDom);
    };

    const onPointerUp = (event) => {
      const drag = dragRef.current;
      if (!drag || drag.pointerId !== event.pointerId) {
        return;
      }

      stopRaf();
      const didMove = drag.hasMoved;
      const nextAnchor = drag.nextAnchor;
      dragRef.current = null;
      blockRef.current?.releasePointerCapture?.(event.pointerId);

      if (didMove) {
        applySubtitleAnchorDom(blockRef.current, nextAnchor);
        commitPosition(nextAnchor);
        return;
      }

      clearSubtitleAnchorDom(blockRef.current);
      setDraftText(text);
      setIsEditing(true);
    };

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onPointerUp);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
      window.removeEventListener("pointercancel", onPointerUp);
      stopRaf();
    };
  }, [applyDragDom, commitPosition, stageRef, stopRaf, text]);

  if (!areEditorSubtitlesEnabled(options) || !activeClip || !text) {
    return null;
  }

  return (
    <Box
      sx={{
        position: "absolute",
        inset: 0,
        zIndex: 6,
        pointerEvents: "none",
        containerType: "size",
      }}
    >
      <Box
        ref={blockRef}
        onPointerDown={isPlaying ? undefined : onBlockPointerDown}
        sx={{
          position: "absolute",
          left: `${anchor.x * 100}%`,
          top: `${anchor.y * 100}%`,
          transform: "translate(-50%, -100%)",
          maxWidth: LINE_MAX_WIDTH,
          width: "max-content",
          textAlign: "center",
          fontFamily: `"${options.fontFamily}", sans-serif`,
          fontWeight: fontStyles.fontWeight,
          fontStyle: fontStyles.fontStyle,
          fontSize: "clamp(11px, 7.4cqi, 28px)",
          lineHeight: isBoxed ? 1.28 : 1.2,
          color: options.subtitleColor,
          pointerEvents: isPlaying ? "none" : "auto",
          cursor: isPlaying ? "default" : isEditing ? "text" : "move",
          userSelect: isEditing ? "text" : "none",
        }}
      >
        {isEditing ? (
          <Box
            component="span"
            sx={
              isBoxed
                ? {
                    display: "inline",
                    backgroundColor: options.backgroundColor,
                    px: "0.4em",
                    py: "0.04em",
                    borderRadius: "0.12em",
                    boxDecorationBreak: "clone",
                    WebkitBoxDecorationBreak: "clone",
                  }
                : { textShadow: TEXT_STROKE }
            }
          >
            <input
              ref={inputRef}
              value={draftText}
              onChange={(event) => setDraftText(event.target.value)}
              onBlur={commitText}
              onKeyDown={(event) => {
                event.stopPropagation();
                if (event.key === "Enter") {
                  event.preventDefault();
                  event.currentTarget.blur();
                }
                if (event.key === "Escape") {
                  event.preventDefault();
                  ignoreBlurRef.current = true;
                  setDraftText(text);
                  setIsEditing(false);
                }
              }}
              onPointerDown={(event) => event.stopPropagation()}
              style={{
                width: `${Math.max(8, draftText.length + 1)}ch`,
                maxWidth: "100%",
                border: 0,
                outline: "none",
                background: "transparent",
                color: "inherit",
                font: "inherit",
                textAlign: "center",
              }}
            />
          </Box>
        ) : (
          <Box
            component="span"
            sx={
              isBoxed
                ? {
                    display: "inline",
                    backgroundColor: options.backgroundColor,
                    px: "0.4em",
                    py: "0.04em",
                    boxDecorationBreak: "clone",
                    WebkitBoxDecorationBreak: "clone",
                    borderRadius: "0.12em",
                  }
                : {
                    display: "inline",
                    textShadow: TEXT_STROKE,
                  }
            }
          >
            {words.map((word, index) => {
              const isActive =
                options.doHighlight &&
                index === activeWordIndex &&
                (isBoxed ||
                  preset === SUBTITLE_PRESET_KARAOKE ||
                  preset === SUBTITLE_PRESET_HIGHLIGHT);
              const wordText = typeof word === "string" ? word : word.word;
              return (
                <Box
                  key={`${wordText}-${index}`}
                  component="span"
                  sx={{
                    display: "inline",
                    color:
                      isActive && !isBoxed
                        ? options.highlightColor
                        : options.subtitleColor,
                    textDecoration:
                      isBoxed && isActive ? "underline" : "none",
                    textUnderlineOffset: "0.08em",
                    textDecorationThickness: "0.07em",
                  }}
                >
                  {wordText}
                  {index < words.length - 1 ? " " : null}
                </Box>
              );
            })}
          </Box>
        )}
      </Box>
    </Box>
  );
};

const VideoEditorSubtitleOverlayMemo = memo(VideoEditorSubtitleOverlay);

export default VideoEditorSubtitleOverlayMemo;
